import SubscriptionStore from "../store/subscription-store";
import {SubscriptionDto} from "../../entity/dto/subscription-dto";
import {CreateSubscriptionResponse} from "../../entity/resource/create-subscription-response";
import {toast} from "react-toastify";
import {RequestBody} from "../../entity/core/request-body";
import {SubscriptionsResponseObject} from "../../entity/resource/subscriptions-response";
import {SubscriptionResource} from "../../entity/resource/subscription-resource";
import {TokenPackageResponse} from "../../entity/resource/token-package-response";
import {CreateCheckoutSessionResponse} from "../../entity/resource/create-checkout-session-response";
import {ConfirmPurchaseResponse} from "../../entity/resource/confirm-purchase-response";
import {TokenBalanceResponse} from "../../entity/resource/token-balance-response";
import {CostTypesResponse} from "../../entity/resource/cost-types-response";

export default class SubscriptionService {
    subscriptionStore: SubscriptionStore;

    constructor(subscriptionStore: SubscriptionStore) {
        this.subscriptionStore = subscriptionStore;
    }

    validateSubscriptions = async (organizationId: string): Promise<boolean> => {
        return await this.subscriptionStore.validateSubscriptions(organizationId);
    }

    getSubscriptions = async (organizationId: string): Promise<SubscriptionsResponseObject[]> => {
        return await this.subscriptionStore.getSubscriptions(organizationId);
    }

    subscribe = async (organizationId: string, isAnnual: boolean, subscriptionDto: SubscriptionDto): Promise<CreateSubscriptionResponse | undefined> => {
        const requestBody: RequestBody = { body: subscriptionDto };
        const response = await this.subscriptionStore.subscribe(organizationId, isAnnual, requestBody);

        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    cancelSubscription = async (organizationId: string, subscriptionId: string): Promise<void> => {
        const response = await this.subscriptionStore.cancelSubscription(organizationId, subscriptionId);

        if (typeof response === 'string')
            toast.error(response);
        else
            toast.success(response.message);
    }

    updatePaymentMethod = async (organizationId: string): Promise<CreateSubscriptionResponse | undefined> => {
        const response = await this.subscriptionStore.updatePaymentMethod(organizationId);

        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    getTokenPackages = async (): Promise<TokenPackageResponse[] | undefined> => {
        const response = await this.subscriptionStore.getTokenPackages();

        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    createCheckoutSession = async (organizationId: string, packageId:string): Promise<CreateCheckoutSessionResponse | undefined> => {
        const response = await this.subscriptionStore.createCheckoutSession(organizationId, packageId);

        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    confirmPurchase = async (organizationId: string, payload: {sessionID: string}): Promise<ConfirmPurchaseResponse | undefined> => {
        const requestBody: RequestBody = { body: payload };
        const response = await this.subscriptionStore.confirmPurchase(organizationId, requestBody);

        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    getTokenBalance = async (organizationId: string): Promise<TokenBalanceResponse | undefined> => {
        const response = await this.subscriptionStore.getTokenBalance(organizationId);

        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    getCostTypes = async (): Promise<CostTypesResponse[] | string | void> => {
        const response = await this.subscriptionStore.getCostTypes();

        if (typeof response === 'string')
            toast.error(response);
        else
            return response;
    }

    getSubscriptionResources = (): SubscriptionResource[] => {
        return [
            {
                feature: 'Data Storage',
                trial: 'Unlimited',
                solo: 'Unlimited',
                enterprise: 'Unlimited'
            },
            {
                feature: 'Create New Title Orders + Searches',
                trial: '50 per month included with ability to add more at $5 per search',
                solo: '50 per month included with ability to add more at $5 per search',
                enterprise: 'Credits Based on Monthly Volume. Unused Credits Rollover'
            },
            {
                feature: 'Mobile Application Tailored for Title',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Chrome Web Extension',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Document Runner',
                trial: '25 credits per month with ability to purchase more credits at $5/credit',
                solo: '25 credits per month with ability to purchase more credits at $5/credit',
                enterprise: 'Credits Based on Monthly Volume. Unused Credits Rollover'
            },
            {
                feature: 'Ability to Custom Name + Tag Documents',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Create Unlimited Custom Templates',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Create Your Own Title Plant',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Access to Zitles Title Plant',
                trial: '25 credits per month. Ability to purchase more credits at $5/credit',
                solo: '25 credits per month. Ability to purchase more credits at $5/credit',
                enterprise: 'Credits Based on Monthly Volume. Unused Credits Rollover'
            },
            {
                feature: 'Import Documents from Title Plant into New Searches',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Create Custom Workflows with Internal + External Teams',
                trial: false,
                solo: false,
                enterprise: true
            },
            {
                feature: 'Receive + Send Title Searches to Clients in Zitles',
                trial: false,
                solo: false,
                enterprise: true
            },
            {
                feature: 'Add comments + notes to title search files',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Order Tracking Dashboard',
                trial: true,
                solo: true,
                enterprise: true
            },
            {
                feature: 'Migrate Historical Data to Your Own Title Plant',
                trial: 'Contact Sales for Pricing',
                solo: 'Contact Sales for Pricing',
                enterprise: 'Contact Sales for Pricing'
            },
            {
                feature: '',
                trial: 'Free - 30 days Free Trial',
                solo: '$100 - user / month',
                enterprise: 'Let`s Talk'
            }
        ]
    }
}